import { Icons } from "@/components/ui/icons";
import { AgentCursor } from "@/workshop/components/cursors/agent-cursor";
import { FloatingToolbar } from "@/workshop/components/canvas/floating-toolbar";
import { FrameHeader } from "@/workshop/components/shapes/shared/frame-header";
import { ConversationFrame } from "@/workshop/components/shapes/conversation-frame/conversation-frame";
import { SearchFrame } from "@/workshop/components/shapes/search-frame/search-frame";
import { ImageFrame } from "@/workshop/components/shapes/image-frame/image-frame";

// ── Frame: Code (local — real FrameHeader, no store deps) ──────

function HeroCodeFrame() {
  return (
    <div
      className="flex flex-col rounded-frame border border-glass-stroke bg-glass-fill shadow-frame backdrop-blur-glass overflow-hidden"
      style={{ width: 200 }}
    >
      <FrameHeader
        title="API Client"
        icon={Icons.Code}
        accentColor="bg-frame-code"
        agentName="Kai"
      />
      <div className="overflow-hidden px-2 py-1.5 font-mono">
        <p className="text-[9px] leading-[1.6]">
          <span style={{ color: "#93C5FD" }}>const </span>
          <span style={{ color: "#E5E7EB" }}>client = </span>
          <span style={{ color: "#86EFAC" }}>new </span>
          <span style={{ color: "#FDE68A" }}>DenkerClient</span>
          <span style={{ color: "#E5E7EB" }}>(key);</span>
        </p>
        <p className="text-[9px] leading-[1.6]">
          <span style={{ color: "#93C5FD" }}>const </span>
          <span style={{ color: "#E5E7EB" }}>res = </span>
          <span style={{ color: "#86EFAC" }}>await </span>
          <span style={{ color: "#E5E7EB" }}>client.</span>
          <span style={{ color: "#FDE68A" }}>query</span>
          <span style={{ color: "#E5E7EB" }}>(prompt);</span>
        </p>
        <p className="text-[9px] leading-[1.6]">
          <span style={{ color: "#93C5FD" }}>return </span>
          <span style={{ color: "#E5E7EB" }}>res.</span>
          <span style={{ color: "#FCA5A5" }}>data</span>
          <span style={{ color: "#E5E7EB" }}>;</span>
        </p>
      </div>
    </div>
  );
}

// ── Mock data ──────────────────────────────────────────────────

const HERO_MESSAGES = [
  { id: "m1", role: "user" as const, content: "Analyse our Q2 metrics" },
  {
    id: "m2",
    role: "assistant" as const,
    content: "Revenue grew 24% MoM, strongest in enterprise…",
  },
];

const HERO_SEARCH_RESULTS = [
  {
    title: "Autonomous Agents in AI: A Survey of Recent Advances",
    snippet:
      "A comprehensive review of multi-agent architectures and emergent behaviours in large-scale deployments.",
    url: "https://arxiv.org",
    isTopPick: true,
  },
  {
    title: "Building Reliable AI Agents with Tool Use",
    snippet:
      "Best practices for grounding, planning, and memory in production agent systems.",
    url: "https://anthropic.com",
  },
];

const HERO_IMAGES = [
  {
    url: "https://picsum.photos/seed/denker-ai/160/90",
    alt: "AI canvas workspace",
  },
  {
    url: "https://picsum.photos/seed/denker-collab/160/90",
    alt: "Team collaboration",
  },
];

// ── Canvas background (full-screen, absolute inset) ────────────

export function CanvasBackground() {
  return (
    <div className="pointer-events-none absolute inset-0" aria-hidden="true">
      {/* Frame 1 — Conversation, top-left */}
      <div
        className="absolute"
        style={{
          top: "7%",
          left: "2%",
          animation: "hero-fade-in 0.6s ease 0.15s both",
        }}
      >
        <div style={{ animation: "hero-float 4.5s ease-in-out 0s infinite" }}>
          <ConversationFrame
            className="w-[200px]"
            title="Research Brief"
            agentName="Aria"
            isStreaming
            messages={HERO_MESSAGES}
          />
        </div>
      </div>
      {/* Aria cursor */}
      <div
        className="absolute"
        style={{
          top: "18%",
          left: "15%",
          animation: "hero-fade-in 0.5s ease 1.5s both",
        }}
      >
        <div style={{ animation: "hero-drift-1 7s ease-in-out 0s infinite" }}>
          <AgentCursor
            agentName="Aria"
            agentColor="#A78BFA"
            content="reading context…"
          />
        </div>
      </div>

      {/* Frame 2 — Code, top-right (shifted left so Kai cursor peeks right) */}
      <div
        className="absolute"
        style={{
          top: "4%",
          right: "7%",
          animation: "hero-fade-in 0.6s ease 0.45s both",
        }}
      >
        <div style={{ animation: "hero-float 4.5s ease-in-out 1.5s infinite" }}>
          <HeroCodeFrame />
        </div>
      </div>
      {/* Kai cursor */}
      <div
        className="absolute"
        style={{
          top: "16%",
          right: "2%",
          animation: "hero-fade-in 0.5s ease 1.9s both",
        }}
      >
        <div style={{ animation: "hero-drift-2 7s ease-in-out 1.8s infinite" }}>
          <AgentCursor
            agentName="Kai"
            agentColor="#60A5FA"
            content="writing code…"
          />
        </div>
      </div>

      {/* Frame 3 — Image, bottom-left (swapped with search) */}
      <div
        className="absolute"
        style={{
          bottom: "12%",
          left: "10%",
          animation: "hero-fade-in 0.6s ease 0.75s both",
        }}
      >
        <div style={{ animation: "hero-float 4.5s ease-in-out 2.2s infinite" }}>
          <div style={{ width: 200 }}>
            <ImageFrame
              title="Campaign Visuals"
              images={HERO_IMAGES}
              status="done"
              agentName="Mia"
            />
          </div>
        </div>
      </div>
      {/* Mia cursor — bottom-left, below the ImageFrame */}
      <div
        className="absolute"
        style={{
          bottom: "4%",
          left: "18%",
          animation: "hero-fade-in 0.5s ease 2.5s both",
        }}
      >
        <div style={{ animation: "hero-drift-3 7s ease-in-out 1.2s infinite" }}>
          <AgentCursor
            agentName="Mia"
            agentColor="#FB923C"
            content="generating images…"
          />
        </div>
      </div>

      {/* Frame 4 — Web Search, middle-right (swapped with image) */}
      <div
        className="absolute"
        style={{
          top: "52%",
          right: "10%",
          animation: "hero-fade-in 0.6s ease 1.05s both",
        }}
      >
        <div style={{ animation: "hero-float 4.5s ease-in-out 0.8s infinite" }}>
          <div style={{ width: 240 }}>
            <SearchFrame
              query="autonomous agents 2026 survey"
              results={HERO_SEARCH_RESULTS}
              status="done"
              agentName="Zara"
            />
          </div>
        </div>
      </div>
      {/* Zara cursor — below the SearchFrame */}
      <div
        className="absolute"
        style={{
          top: "82%",
          right: "2%",
          animation: "hero-fade-in 0.5s ease 2.2s both",
        }}
      >
        <div style={{ animation: "hero-drift-4 7s ease-in-out 0.6s infinite" }}>
          <AgentCursor
            agentName="Zara"
            agentColor="#F472B6"
            content="searching web…"
          />
        </div>
      </div>

      {/* Tagline watermark */}
      <div
        className="absolute left-[3%]"
        style={{ top: "50%", transform: "translateY(-50%)" }}
      >
        <div
          className="flex flex-col leading-none"
          style={{
            animation: "hero-fade-in 0.8s ease 1.2s both",
            opacity: 0.14,
          }}
        >
          {["Think.", "Create.", "Together."].map((w) => (
            <span
              key={w}
              className="block text-[2.4rem] font-bold tracking-tight text-accent"
            >
              {w}
            </span>
          ))}
        </div>
      </div>

      {/* Floating toolbar — bottom-right */}
      <div
        className="absolute bottom-[3%] right-[2%]"
        style={{ animation: "hero-fade-in 0.6s ease 2s both" }}
      >
        <FloatingToolbar zoomLevel={100} />
      </div>
    </div>
  );
}
